import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import rootReducer from './reducers';
import { switchStyle } from './switchStyle.middleware';
import { DEFAULT_FALLBACK_LANGUAGE } from 'Constants/App';

// Object defaultWidgetSet is used both as a model when you want to create a new widget, and for
// checking types, the values determined here indicate the type of data that can be entered in each key
export const defaultWidgetSet = {
    // settings() method keys
    config: {
        id            : '',
        ipAddress     : '',
        channelId     : '',
        // For multi property mode, these are filters by country  codes and city name.
        hotelsFilters : {
            countryCode : '',
            city        : '',
        },
        isMultiProperty        : false,
        isSetDisabled          : false,
        jsonFile               : '', // CDN url where widget config may be found, optional,
        cutoff                 : 3, // Used to compute checkIn. Day's length from today
        los                    : 5, // Length of stay. Used to compute checkOut
        styles                 : '', // Inline styles
        domain                 : '', // Valid KBE domain
        currencyDisplay        : '', // Display currency with symbol or ISO code
        language               : '', // It will replace the one coming from the NTP
        checkIn                : '',
        checkOut               : '',
        propertyCode           : '',
        childrenEnabled        : false,
        promotionalCodeEnabled : false,
        maxCheckOutDate        : '',
        maxChildAge            : 6,
        minChildAge            : 0,
        maxOccupancy           : 12,
        maxRoomsPerBooking     : null,
        minOccupancy           : 1,
        defaultOccupancy       : [ {} ],
        currency               : {}, // It will replace the one coming from the NTP
        defaultCurrency        : {},
        availableCurrencies    : {},
        availableLanguages     : [],
        filterByPrice          : true, // Hide priceless rates / offers
        // If set to native, the offers will be sorted by their order in the Hotel Manager, otherwise by price
        sortOrder              : 'native',
        onViewRooms            : undefined,
        onSearchBarClick       : undefined,
        onPropertiesRendered   : undefined,
        onWidgetLoaded         : () => {},
        widgetType             : '', // If is an 'echo' widget,  some endpoints will be at EE
        newTabDisabled         : false,
        echoApiUrl             : '',
        priceDisplayMode       : '',
        priceCalcMode          : '',
        isConnected            : false,
        searchButtonEnabled    : false,
        urlTransformers        : null,
    },
    hotelProfile: {
        container  : 'data-kbe-hotelprofile',
        isDisabled : false,
        isRendered : false, // Setting it to false from outside the app will cause the component to be unmounted
    },
    searchbar: {
        hideDatePickerSelector         : false,
        hideOccupancySelector          : false,
        showCurrencySelector           : true,
        showLanguageSelector           : true,
        destinationsFilterEnabled      : false,
        autocompleteFilterEnabled      : false,
        shouldCheckDestinationSelected : false, // Some hotels require a destination filter to be selected in searchbar for some flows
        searchButtonEnabled            : false,
        fullWidth                      : false, // The bar will occupy the entire width of your container or will have a maximum
        isConnected                    : false, // Indicates whether the changes will be reflected in the rates and offers components
        isDisabled                     : false,
        isRendered                     : false, // Setting it to false from outside the app will cause the component to be unmounted
        container                      : 'data-kbe-searchbar', // Where the corresponding widgetSet will be rendered
    },
    rates: {
        layout          : 1,
        loadingBlocks   : 0, // The number of blocks displayed until the server response arrives
        showDescription : true,
        showSpecs       : true,
        showFilters     : false,
        sortOrder       : 'native', // If set to “native”, the offers will be sorted by their order in the Hotel Manager, otherwise by price
        filterByCode    : '',
        isDisabled      : false,
        shouldShowLogin : true,
        isRendered      : false, // Setting it to false from outside the app will cause the component to be unmounted
        container       : 'data-kbe-rates', // Where the corresponding widgetSet will be rendered
        roomCodes       : '',
        categories      : '',
        availableOffersOnly: false,
    },
    offers: {
        layout        : 2,
        loadingBlocks : 0, // The number of blocks displayed until the server response arrives
        showTeaser    : true,
        showFilters   : false,
        sortOrder     : 'native', // If set to “native”, the offers will be sorted by their order in the Hotel Manager, otherwise by price
        filterByCode  : '',
        isDisabled    : false,
        isRendered    : false, // Setting it to false from outside the app will cause the component to be unmounted
        container     : 'data-kbe-offers', // Where the corresponding widgetSet will be rendered
        packageCodes  : '',
        categories    : '',
        availableOffersOnly: false,
        numberOfOffers: 3,
    },
    hotels: {
        priceDisplayMode          : '',
        priceCalcMode             : '',
        pageSize                  : 10,
        page                      : 1,
        styledPrice               : false,
        showPrices                : true,
        showUnavailable           : false,
        isRendered                : false,
        isDisabled                : false,
        showFilters               : true,
        showViewSelector          : true,
        showSortingSelector       : false,
        layout                    : 'list',
        defaultLayout             : 'list',
        view                      : '',
        showFeedbackMessage       : false, // Message that is shown when no available or non-available hotels are available
        availableLayouts          : [],
        container                 : 'data-kbe-hotelslist',
        autocompleteFilterEnabled : false,
    },
    rewardProgram: {
        container      : 'data-kbe-reward-program',
        isRendered     : false,
        isDisabled     : false,
        channelId      : '',
        actionUrl      : '',
        currencyCode   : '',
        fallbackAmount : 0,
        layout         : 'vertical',
    },
    inquiry: {
        container      : 'data-kbe-inquiry',
        isRendered     : false,
        isDisabled     : false,
        layout         : 1,
    },
};

const initState = {
    search: {
        isLoading : true,
        data      : {
            checkIn           : null,
            checkOut          : null,
            language          : DEFAULT_FALLBACK_LANGUAGE,
            currency          : null,
            occupancy         : undefined,
            defaultOccupancy  : undefined,
            clientPromoCode   : '',
            shouldUpgradeTier : false,
            shouldHideHotel   : false,
        },
        error: false,
    },
    settings: {
        isLoading : false,
        data      : {
            language               : DEFAULT_FALLBACK_LANGUAGE,
            userAgent              : '',
            checkIn                : '',
            checkOut               : '',
            maxCheckOutDate        : '',
            minOccupancy           : 1,
            maxOccupancy           : 5,
            maxChildAge            : 0,
            minChildAge            : 0,
            maxRoomsPerBooking     : null,
            childrenEnabled        : false,
            promotionalCodeEnabled : false,
            availableLanguages     : [],
            availableCurrencies    : {},
            defaultCurrency        : null,
            onWidgetLoaded         : () => {},
            channelId              : '',
            echoWidget             : false, // If is an echo widget, some endpoints will be at EE.
            // FIXME: this is deprecated by widgetType key, delete it securely
            userCountry            : '', // Used for Google or Baidu maps
            isMultiProperty        : false,
            // For multi property mode, these are filters by country codes and city name. All the widgets may use it.
            hotelsFilters          : {
                countryCode : '',
                city        : '',
            },
            newTabDisabled: false,
            priceCalcMode: '',
            priceDisplayMode: '',
        },
        error   : false,
        dataSet : false,
    },
    translations: {
        isLoading : false,
        data      : {},
        error     : false,
    },
    destinations : {},
    widgetPool   : [ defaultWidgetSet ],
    trustYou     : {},
};

/**
 * This middleware validates the type and existence of the keys that the user
 * want to set in the configuration
 */
const keyValidator = store => next => (action) => {
    if (action.type === 'SET_CONFIG') {
        const { widgetType, config } = action.payload;
        // Position 0 is used as a reference for object keys and types
        const widgetSet = store.getState().widgetPool[0];

        // We save all the errors found in the action. If the key's type is function, we don't check for types equality
        // (So then in the component we can check if the function exists)
        const keyValidationErrors = Object.keys(config)
            .map((key) => {
                const isFunction = typeof config[key] === 'function';
                const isUndefined = config[key] === undefined; // It may be expected that some keys are set as undefined
                const keyInWidgetSet = widgetType in widgetSet && key in widgetSet[widgetType];
                const expectedType = typeof widgetSet[widgetType][key];
                const actualType = typeof config[key];
                const keysHaveSameType = expectedType === actualType;

                return {
                    key,
                    isFunction,
                    isUndefined,
                    keyInWidgetSet,
                    keysHaveSameType,
                    expectedType,
                    actualType,
                    isValid: keyInWidgetSet && (isFunction || isUndefined || keysHaveSameType),
                };
            })
            .filter(err => !err.isValid);

        if (keyValidationErrors.length > 0) {
            const errorMsg = keyValidationErrors.map((err) => {
                if (!err.keyInWidgetSet) {
                    return `Key ${err.key} is not part of the available options. Please check the documentation in the hotel manager.`;
                }
                // eslint-disable-next-line max-len
                return `Key ${err.key} is neither a function nor undefined and also differs from the expected type. Expected: ${err.expectedType}, Actual: ${err.actualType}`;
            }).join('\n\n');

            /* eslint-disable no-console */
            console.warn(`[KBE-Widget] Please check the keys of "${widgetType}" with id="${action.id}":\n\n${errorMsg}`);

            // Remove invalid keys
            keyValidationErrors.forEach(err => delete config[err.key]);
        }
    }

    return next(action);
};

const composeEnhancers = composeWithDevTools({ trace: true, traceLimit: 25 });

export default function configureStore(initialState = initState) {
    return createStore(
        rootReducer,
        initialState,
        composeEnhancers(
            applyMiddleware(
                keyValidator,
                switchStyle,
                thunk,
            ),
        ),
    );
}
